import React, { useState, useRef } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { DateTime } from "luxon";
import {
  validateAddressInServiceArea,
  getAppointmentForManagement,
  cancelAppointment,
  requestReschedule,
} from "../api/publicBookingApi";
import PublicBookingLayout from "../components/PublicBooking/PublicBookingLayout";
import AppointmentSetupStep from "../components/PublicBooking/AppointmentSetupStep";
import RescheduleWorkflowSidebar from "../components/AppointmentManagement/RescheduleWorkflowSidebar";
import FloatingRescheduleButton from "../components/AppointmentManagement/FloatingRescheduleButton";
import ThemedButton from "../components/Shared/ThemedButton";
import { getBookingPageTheme } from "../utility/themeUtils";
import { isDarkMode, getThemedBg } from "../utility/themeVars";
import { getOriginalTimeDisplay } from "../utility/timezoneUtils";
import { formatDateTime as formatSelectedDateTime } from "../utility/bookingUtils";
import { parsePrice, formatPriceWithCurrency } from "../utility/priceUtils";

const AppointmentManagementPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showRescheduleFlow, setShowRescheduleFlow] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [actionCompleted, setActionCompleted] = useState(null);
  const [isOpeningRescheduleFlow, setIsOpeningRescheduleFlow] = useState(false);

  // Reschedule flow state
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [travelFee, setTravelFee] = useState(null);
  const [customTimeRequested, setCustomTimeRequested] = useState(false);
  const [isAvailableSlot, setIsAvailableSlot] = useState(false);

  // Reschedule-specific state
  const [rescheduleAvailabilityIds, setRescheduleAvailabilityIds] = useState(
    []
  );
  const [rescheduleLocationInfo, setRescheduleLocationInfo] = useState(null);
  const [locationMismatchError, setLocationMismatchError] = useState(false);
  const rescheduleWorkflowRef = useRef(null);
  const [showRescheduleConfirmModal, setShowRescheduleConfirmModal] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);

  const hasInvalidLink = !token;

  // Fetch appointment details
  const {
    data: appointmentData,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ["appointment-management", slug, token],
    queryFn: () => getAppointmentForManagement(slug, token),
    enabled: !!(token && slug),
    retry: false,
  });

  // Cancel appointment mutation
  const cancelMutation = useMutation({
    mutationFn: ({ appointmentId, token, reason }) =>
      cancelAppointment(slug, appointmentId, token, reason),
    onSuccess: (data) => {
      setActionCompleted({
        type: "cancel",
        message: "Your appointment has been cancelled successfully.",
      });
      setShowCancelForm(false);
    },
    onError: (error) => {
      console.error("Cancel error:", error);
    },
  });

  // Reschedule request mutation
  const rescheduleMutation = useMutation({
    mutationFn: ({
      appointmentId,
      token,
      newDate,
      newTime,
      clientTimezone,
      isAvailableSlot,
    }) =>
      requestReschedule(
        slug,
        appointmentId,
        token,
        newDate,
        newTime,
        clientTimezone,
        "",
        isAvailableSlot
      ),
    onSuccess: (data) => {
      const isAutoConfirmed = data.appointment?.status === "confirmed";
      if (data.managementToken) {
        setSearchParams({ token: data.managementToken }, { replace: true });
      }
      setActionCompleted({
        type: isAutoConfirmed ? "reschedule" : "reschedule-pending",
        message: isAutoConfirmed
          ? "Your appointment has been rescheduled successfully. You'll receive a confirmation email with your updated appointment details."
          : "Your reschedule request has been submitted. Keep your original appointment free until we confirm the new time — you'll get an email when it's approved.",
      });
      setShowRescheduleFlow(false);
    },
    onError: (error) => {
      console.error("Reschedule error:", error);
      setRescheduleError(error.message || "Something went wrong. Please try again.");
    },
  });

  const handleCancel = () => {
    if (!appointmentData?.appointment?.id) return;

    cancelMutation.mutate({
      appointmentId: appointmentData.appointment.id,
      token,
      reason: cancellationReason.trim(),
    });
  };

  // Initialize reschedule flow
  const handleOpenRescheduleFlow = async () => {
    setIsOpeningRescheduleFlow(true);

    try {
      const appointment = appointmentData?.appointment;
      if (appointment) {
        // Pre-populate with current appointment data
        setSelectedAppointmentType(appointment.type);
        if (appointment.travelFee) {
          setTravelFee(parseFloat(appointment.travelFee));
        }

        // For mobile appointments, validate the existing address to get availability IDs
        if (
          appointment.type === "mobile" &&
          appointment.serviceAddress?.placeId
        ) {
          try {
            const validationResult = await validateAddressInServiceArea(
              slug,
              appointment.serviceAddress.placeId
            );
            setRescheduleAvailabilityIds(
              validationResult.matchingAvailabilityIds || []
            );
            setRescheduleLocationInfo({
              address: appointment.serviceAddress.address,
              timeZoneId: validationResult.timeZoneId,
            });
          } catch (error) {
            console.error("Failed to validate reschedule address:", error);
            // Fallback - still allow reschedule but with empty availability
            setRescheduleAvailabilityIds([]);
            setRescheduleLocationInfo({
              address: appointment.serviceAddress.address,
              timeZoneId: null,
            });
          }
        } else if (appointment.type === "fixed") {
          // For fixed appointments, match the appointment's placeId with available fixed locations
          if (
            appointment.serviceAddress?.placeId &&
            appointmentData?.fixedLocationData?.locations
          ) {
            const matchingLocation =
              appointmentData.fixedLocationData.locations.find(
                (location) =>
                  location.placeId === appointment.serviceAddress.placeId
              );

            if (matchingLocation) {
              // Pre-select the matching location
              setSelectedLocation(matchingLocation);
              setRescheduleLocationInfo({
                address:
                  appointment.serviceAddress.address ||
                  matchingLocation.address,
                timeZoneId: matchingLocation.timeZoneId,
              });
              setLocationMismatchError(false);
            } else {
              // No matching location found - set error state
              setLocationMismatchError(true);
              setSelectedLocation(null);
              setRescheduleLocationInfo({
                address:
                  appointment.serviceAddress.address ||
                  "Location not available",
                timeZoneId: null,
              });
            }
          }

          // Don't set reschedule availability IDs for fixed appointments
          setRescheduleAvailabilityIds([]);
        }
      }

      // Reset reschedule-specific state
      setSelectedDate(null);
      setSelectedTime(null);
      setCustomTimeRequested(false);
      setIsAvailableSlot(false);
      setShowRescheduleFlow(true);
    } finally {
      setIsOpeningRescheduleFlow(false);
    }
  };

  // Intercept reschedule submit — show confirmation modal for auto-confirm path (F-16)
  const handleRescheduleSubmitIntent = () => {
    const willAutoConfirm = isAvailableSlot && !customTimeRequested && bookingPage?.autoApproval !== false;
    if (willAutoConfirm) {
      setShowRescheduleConfirmModal(true);
    } else {
      handleSubmitReschedule();
    }
  };

  // Handle reschedule submission
  const handleSubmitReschedule = () => {
    if (!appointmentData?.appointment?.id || !selectedDate || !selectedTime)
      return;
    setRescheduleError(null);

    // Extract date and time from the selected DateTime
    const dateTime = DateTime.fromJSDate(selectedDate).set({
      hour: parseInt(selectedTime.split(":")[0]),
      minute: parseInt(selectedTime.split(":")[1]),
    });

    // Convert to UTC for consistency with public booking pattern
    const utcDateTime = dateTime.toUTC();
    const newDate = utcDateTime.toISO(); // Send as UTC ISO string
    const newTime = dateTime.toFormat("HH:mm"); // Local time format
    const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    rescheduleMutation.mutate({
      appointmentId: appointmentData.appointment.id,
      token,
      newDate,
      newTime,
      clientTimezone,
      isAvailableSlot: isAvailableSlot && !customTimeRequested,
    });
  };

  // Handle back from reschedule flow
  const handleBackFromReschedule = () => {
    setShowRescheduleFlow(false);
    // Reset all reschedule state
    setSelectedAppointmentType(null);
    setSelectedAddress(null);
    setSelectedLocation(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setTravelFee(null);
    setCustomTimeRequested(false);
    setIsAvailableSlot(false);
    setRescheduleAvailabilityIds([]);
    setRescheduleLocationInfo(null);
    setLocationMismatchError(false);
  };

  // Handle time selection - track if it's from available slots
  const handleTimeChange = (time, fromAvailableSlots = false) => {
    setSelectedTime(time);
    setIsAvailableSlot(fromAvailableSlots);
  };

  const formatDateTime = (isoString, appointmentTimeZoneId) => {
    if (!isoString) return "N/A";

    // Use timezone-aware formatting with 12-hour format
    const originalTimeDisplay = getOriginalTimeDisplay(
      isoString,
      appointmentTimeZoneId,
      true // Always use 12-hour format
    );

    // If no timezone difference, format using local time with 12-hour format
    if (!originalTimeDisplay) {
      const date = new Date(isoString);
      return date.toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }

    return originalTimeDisplay;
  };


  // Get theme configuration with safe fallbacks
  const appointment = appointmentData?.appointment;
  const bookingPage = appointmentData?.bookingPage;
  const { primaryColor, themeMode } = getBookingPageTheme(bookingPage);
  const isDark = isDarkMode(themeMode);
  const themedBg = getThemedBg(themeMode, primaryColor);

  // Get fixed location data from appointment data for fixed appointments
  const fixedLocationData =
    appointment?.type === "fixed" && appointmentData?.fixedLocationData
      ? appointmentData.fixedLocationData
      : { locations: [] };

  if (hasInvalidLink) {
    return (
      <PublicBookingLayout>
        <div className="min-h-screen flex items-center justify-center py-8">
          <div className="max-w-md mx-auto bg-themed-card border-themed rounded-card shadow-hairline border p-8 text-center">
            <div className="text-signal-amber-500 mb-4" aria-hidden="true">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-themed-primary mb-2">
              This link is no longer valid
            </h3>
            <p className="text-themed-secondary mb-4">
              Management links expire after use or after a set period. Check your most recent booking confirmation email for a fresh link.
            </p>
            {slug && (
              <a
                href={`/book/${slug}`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md bg-accent-600 hover:bg-accent-700"
              >
                Book a new appointment
              </a>
            )}
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  if (isLoading) {
    return (
      <PublicBookingLayout>
        <div
          className="py-8"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <span className="sr-only">Loading appointment details</span>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-themed-card border-themed rounded-card shadow-hairline border overflow-hidden animate-pulse">
              <div className="px-6 py-6 border-b border-themed">
                <div className="h-3 w-32 bg-ink-100 rounded mb-3" />
                <div className="h-7 w-48 bg-ink-100 rounded mb-4" />
                <div className="h-6 w-28 bg-ink-100 rounded-full" />
              </div>
              <div className="px-6 py-6 space-y-6">
                <div className="space-y-3">
                  <div className="h-4 w-40 bg-ink-100 rounded" />
                  <div className="h-4 w-full bg-ink-100 rounded" />
                  <div className="h-4 w-3/4 bg-ink-100 rounded" />
                </div>
                <div className="space-y-3">
                  <div className="h-4 w-24 bg-ink-100 rounded" />
                  <div className="h-4 w-full bg-ink-100 rounded" />
                  <div className="h-4 w-5/6 bg-ink-100 rounded" />
                </div>
                <div className="border-t border-themed pt-6 space-y-3">
                  <div className="h-12 w-full bg-ink-100 rounded-pill" />
                  <div className="h-4 w-32 bg-ink-100 rounded mx-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  if (isError) {
    return (
      <PublicBookingLayout primaryColor={primaryColor} themeMode={themeMode}>
        <div className="min-h-screen flex items-center justify-center py-8" role="alert">
          <div className="max-w-md mx-auto bg-themed-card border-themed rounded-card shadow-hairline border p-8 text-center">
            <div className="text-signal-red-500 mb-4" aria-hidden="true">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-themed-primary mb-2">
              Unable to Load Appointment
            </h3>
            <p className="text-themed-secondary mb-4">
              {error?.message ||
                "The management link is invalid or has expired."}
            </p>
            <p className="text-sm text-themed-secondary mb-4">
              Check your booking confirmation email for a valid link.
            </p>
            {slug && (
              <a
                href={`/book/${slug}`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md bg-accent-600 hover:bg-accent-700"
              >
                Book a new appointment
              </a>
            )}
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  if (actionCompleted) {
    const businessName = bookingPage?.businessName;
    return (
      <PublicBookingLayout primaryColor={primaryColor} themeMode={themeMode}>
        <div className="min-h-screen flex items-center justify-center py-8" role="status" aria-live="polite">
          <div className="max-w-lg mx-auto bg-themed-card border-themed rounded-card shadow-hairline border p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-signal-green-100 rounded-full flex items-center justify-center mb-6" aria-hidden="true">
              <svg
                className="w-8 h-8 text-signal-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="font-display text-2xl text-themed-primary mb-3">
              {actionCompleted.type === "cancel"
                ? "Appointment cancelled"
                : actionCompleted.type === "reschedule-pending"
                ? "Reschedule requested"
                : "Appointment rescheduled"}
            </h1>
            <p className="text-themed-secondary leading-relaxed">
              {actionCompleted.message}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              {actionCompleted.type !== "cancel" && slug && token && (
                <a
                  href={`/manage-booking/${slug}?token=${token}`}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-themed rounded-pill text-themed-primary bg-themed-card hover:bg-opacity-80"
                >
                  View appointment
                </a>
              )}
              {slug && (
                <a
                  href={`/book/${slug}`}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-pill bg-accent-600 hover:bg-accent-700"
                >
                  Book another appointment
                </a>
              )}
            </div>
            {actionCompleted.type === "cancel" && businessName && (
              <p className="mt-6 text-xs text-themed-secondary">
                Cancelled by mistake? Contact <span className="font-medium text-themed-primary">{businessName}</span> to discuss your options.
              </p>
            )}
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  return (
    <PublicBookingLayout primaryColor={primaryColor} themeMode={themeMode}>
      <div className="py-8">
        {/* Back button + mobile step indicator for reschedule flow */}
        {showRescheduleFlow && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleBackFromReschedule}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-themed-primary bg-themed-card border border-themed rounded-pill hover:bg-opacity-80 focus:outline-none focus-themed transition-colors duration-200"
            >
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="hidden sm:inline">Back to Appointment Details</span>
              <span className="sm:hidden">Back</span>
            </button>

            {/* Mobile-visible step indicator (sidebar carries the same on desktop) */}
            <div
              className="lg:hidden flex items-center space-x-2 smallcaps tracking-wider text-themed-secondary"
              aria-hidden="true"
            >
              <span
                className={
                  !selectedDate || !selectedTime
                    ? "text-themed-primary"
                    : ""
                }
              >
                1 · Pick a time
              </span>
              <span>→</span>
              <span
                className={
                  selectedDate && selectedTime
                    ? "text-themed-primary"
                    : ""
                }
              >
                2 · Confirm
              </span>
            </div>
          </div>
        )}

        {showRescheduleFlow ? (
          <>
            {/* Location Mismatch Error */}
            {locationMismatchError && (
              <div
                role="alert"
                className="rounded-card border p-4 mb-6 bg-signal-red-50 border-signal-red-100 max-w-4xl mx-auto"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1" aria-hidden="true">
                    <svg
                      className="w-5 h-5 text-signal-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-medium text-signal-red-700 mb-1">
                      Location no longer available
                    </h2>
                    <p className="text-sm text-signal-red-700 mb-2">
                      The original location for this appointment is no longer available for booking.
                      {bookingPage?.businessName && (
                        <> Please contact <strong>{bookingPage.businessName}</strong> directly to reschedule.</>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={handleBackFromReschedule}
                      className="text-sm font-medium text-signal-red-700 underline hover:text-signal-red-600 px-2 py-2 rounded-pill focus:outline-none focus-themed"
                    >
                      Back to appointment details
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Reschedule Flow Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 min-h-screen lg:min-h-0">
              {/* Left Column - Reschedule Content - 70% (7 columns on lg) */}
              <div className="lg:col-span-7 lg:order-1 flex flex-col">
                {/* F-17: Services pre-populated note */}
                {appointmentData?.appointment?.services?.length > 0 && (
                  <div className="mb-4 px-4 py-3 bg-themed-card border border-themed rounded-card text-sm text-themed-secondary">
                    Rescheduling will keep your original services:{" "}
                    <span className="text-themed-primary font-medium">
                      {appointmentData.appointment.services.map(s => s.name).join(", ")}
                    </span>
                    . To change services, cancel and book a new appointment.
                  </div>
                )}
                <AppointmentSetupStep
                  serviceLocationTypes={{
                    hasFixed: appointmentData?.appointment?.type === "fixed",
                    hasMobile: appointmentData?.appointment?.type === "mobile",
                  }}
                  selectedAppointmentType={selectedAppointmentType}
                  onAppointmentTypeChange={setSelectedAppointmentType}
                  selectedAddress={selectedAddress}
                  onAddressChange={setSelectedAddress}
                  selectedLocation={selectedLocation}
                  onLocationChange={setSelectedLocation}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  selectedTime={selectedTime}
                  onTimeChange={handleTimeChange}
                  primaryColor={primaryColor}
                  themeMode={themeMode}
                  businessName={appointmentData?.bookingPage?.businessName}
                  bookingPage={appointmentData?.bookingPage}
                  slug={slug}
                  travelFee={travelFee}
                  onTravelFeeChange={setTravelFee}
                  currency={appointmentData?.appointment?.currency || "CAD"}
                  fixedLocationData={fixedLocationData}
                  onCustomTimeRequestChange={setCustomTimeRequested}
                  selectedServices={
                    appointmentData?.appointment?.services?.map((service) => ({
                      service: {
                        _id: service.name,
                        name: service.name,
                        duration: service.duration,
                        price: parsePrice(service.price),
                      },
                      quantity: service.quantity,
                    })) || []
                  }
                  totalServiceDuration={
                    appointmentData?.appointment?.services?.reduce(
                      (total, service) =>
                        total + service.duration * service.quantity,
                      0
                    ) || 0
                  }
                  isRescheduleMode={true}
                  prePopulatedAvailabilityIds={rescheduleAvailabilityIds}
                  rescheduleLocationInfo={rescheduleLocationInfo}
                  appointment={appointmentData?.appointment}
                  isFixedRescheduleMode={
                    appointmentData?.appointment?.type === "fixed" &&
                    selectedLocation
                  }
                  locationMismatchError={locationMismatchError}
                />
              </div>

              {/* Right Column - Reschedule Sidebar - 30% (3 columns on lg) - Desktop only */}
              {!locationMismatchError && (
                <div className="hidden lg:flex lg:col-span-3 lg:order-2 flex-col w-full">
                  <RescheduleWorkflowSidebar
                    bookingPage={appointmentData?.bookingPage}
                    appointment={appointmentData?.appointment}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    isAvailableSlot={isAvailableSlot && !customTimeRequested}
                    customTimeRequested={customTimeRequested}
                    onSubmitReschedule={handleRescheduleSubmitIntent}
                    disabled={
                      !selectedDate || !selectedTime || locationMismatchError
                    }
                    isSubmitting={rescheduleMutation.isPending}
                    primaryColor={primaryColor}
                    themeMode={themeMode}
                  />
                </div>
              )}
            </div>

            {/* Mobile-only Reschedule Sidebar - appears at bottom */}
            {!locationMismatchError && (
              <div
                ref={rescheduleWorkflowRef}
                className="block lg:hidden mt-8 mb-20"
              >
                <RescheduleWorkflowSidebar
                  bookingPage={appointmentData?.bookingPage}
                  appointment={appointmentData?.appointment}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  isAvailableSlot={isAvailableSlot && !customTimeRequested}
                  customTimeRequested={customTimeRequested}
                  onSubmitReschedule={handleRescheduleSubmitIntent}
                  disabled={
                    !selectedDate || !selectedTime || locationMismatchError
                  }
                  isSubmitting={rescheduleMutation.isPending}
                  primaryColor={primaryColor}
                  themeMode={themeMode}
                />
              </div>
            )}

            {/* Floating Reschedule Button for Mobile */}
            {!locationMismatchError && (
              <FloatingRescheduleButton
                bookingPage={appointmentData?.bookingPage}
                appointment={appointmentData?.appointment}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                isAvailableSlot={isAvailableSlot && !customTimeRequested}
                customTimeRequested={customTimeRequested}
                onSubmitReschedule={handleRescheduleSubmitIntent}
                disabled={
                  !selectedDate || !selectedTime || locationMismatchError
                }
                isSubmitting={rescheduleMutation.isPending}
                primaryColor={primaryColor}
                themeMode={themeMode}
                rescheduleWorkflowRef={rescheduleWorkflowRef}
              />
            )}

            {rescheduleError && (
              <div
                role="alert"
                aria-live="assertive"
                className="mt-4 mx-4 sm:mx-0 p-3 bg-signal-red-50 border border-signal-red-100 rounded-md"
              >
                <p className="text-sm text-signal-red-700">
                  {rescheduleError}
                </p>
                <button
                  type="button"
                  onClick={handleSubmitReschedule}
                  disabled={rescheduleMutation.isPending}
                  className="mt-2 text-xs font-medium text-signal-red-700 underline hover:text-signal-red-600 disabled:opacity-50"
                >
                  Try again
                </button>
              </div>
            )}
          </>
        ) : (
          /* Original Appointment Details Layout */
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="bg-themed-card border-themed rounded-card shadow-hairline border overflow-hidden"
            >
              {/* Header */}
              <div
                className="bg-themed-card px-6 py-6 border-b border-themed"
              >
                {bookingPage?.businessName && (
                  <p className="text-sm font-medium text-themed-secondary mb-1 break-words">
                    {bookingPage.businessName}
                  </p>
                )}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-1">
                  <h1 className="font-display text-3xl text-themed-primary tracking-display">
                    Your Appointment
                  </h1>
                  <span
                    role="status"
                    className={`inline-flex w-fit px-3 py-1 rounded-pill text-sm font-medium ${
                      appointment.status === "confirmed"
                        ? isDark
                          ? "bg-signal-green-500/20 text-signal-green-100 border border-signal-green-500"
                          : "bg-signal-green-50 text-signal-green-600 border border-signal-green-100"
                        : appointment.status === "cancelled"
                        ? isDark
                          ? "bg-signal-red-500/20 text-signal-red-100 border border-signal-red-500"
                          : "bg-signal-red-50 text-signal-red-700 border border-signal-red-100"
                        : isDark
                        ? "bg-signal-amber-500/20 text-signal-amber-100 border border-signal-amber-500"
                        : "bg-signal-amber-50 text-signal-amber-600 border border-signal-amber-100"
                    }`}
                  >
                    <span className="sr-only">Appointment status: </span>
                    {appointment.status
                      ? ({
                          confirmed: "Confirmed",
                          cancelled: "Cancelled",
                          pending: "Awaiting Confirmation",
                        }[appointment.status] ||
                          (appointment.status.charAt(0).toUpperCase() +
                            appointment.status.slice(1)))
                      : "Unknown"}
                  </span>
                </div>
                {appointment._id && (
                  <p className="text-themed-secondary">
                    <span className="smallcaps">Ref</span>{" "}
                    <span className="font-mono tabular text-sm tracking-wide text-themed-primary">
                      {String(appointment._id).slice(0, 8).toUpperCase()}
                    </span>
                  </p>
                )}
              </div>

              {/* Appointment Details */}
              <div className="px-6 py-6">
                <div className="mb-6">
                  <h2 className="text-base font-medium text-themed-primary mb-4">
                    Appointment Details
                  </h2>

                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between">
                      <span className="font-medium text-themed-secondary">
                        Date &amp; Time
                      </span>
                      <span className="text-themed-primary tabular">
                        {formatDateTime(
                          appointment.startTime,
                          appointment.timeZoneId
                        )}
                      </span>
                    </div>

                    {appointment.serviceAddress && (
                      <div className="flex flex-col sm:flex-row sm:justify-between">
                        <span className="font-medium text-themed-secondary">
                          Location
                        </span>
                        <span className="text-themed-primary sm:text-right sm:max-w-[60%]">
                          {appointment.serviceAddress.full}
                        </span>
                      </div>
                    )}

                  </div>
                </div>

                {/* Services */}
                <div className="mb-6">
                  <h2 className="text-base font-medium text-themed-primary mb-3">
                    Services
                  </h2>
                  <div className="divide-y divide-themed">
                    {appointment.services.map((service, index) => (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row sm:justify-between py-2"
                      >
                        <span className="text-themed-secondary">
                          {service.quantity > 1 && `${service.quantity}× `}
                          {service.name}
                        </span>
                        <span className="text-themed-primary tabular">
                          {formatPriceWithCurrency(
                            parsePrice(service.price) * service.quantity,
                            appointment.currency,
                            true
                          )}
                        </span>
                      </div>
                    ))}
                    {parseFloat(appointment.travelFee) > 0 && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2">
                        <span className="text-themed-secondary">
                          Distance fee
                        </span>
                        <span className="text-themed-primary tabular">
                          {formatPriceWithCurrency(
                            appointment.travelFee,
                            appointment.currency,
                            true
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between border-t border-themed pt-4 mt-4">
                    <span className="font-medium text-themed-secondary">
                      Total
                    </span>
                    <span className="font-display-tight text-2xl text-themed-primary tabular">
                      {formatPriceWithCurrency(
                        appointment.totalPrice,
                        appointment.currency,
                        true
                      )}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                {appointment.status !== "cancelled" && (
                  <div className="border-t border-themed pt-6 space-y-3">
                    {appointment.canReschedule && (
                      <ThemedButton
                        onClick={handleOpenRescheduleFlow}
                        disabled={isOpeningRescheduleFlow}
                        primaryColor={primaryColor}
                        themeMode={themeMode}
                        variant="primary"
                        className="w-full flex items-center justify-center py-3"
                      >
                        {isOpeningRescheduleFlow ? (
                          <div
                            className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"
                            aria-hidden="true"
                          ></div>
                        ) : (
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        )}
                        {isOpeningRescheduleFlow
                          ? "Loading..."
                          : "Reschedule Appointment"}
                      </ThemedButton>
                    )}

                    {appointment.canCancel && (
                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => setShowCancelForm(true)}
                          className="text-sm text-signal-red-500 hover:text-signal-red-700 hover:underline transition-colors px-3 py-3 rounded-pill focus:outline-none focus-themed"
                        >
                          Cancel Appointment
                        </button>
                      </div>
                    )}

                    {!appointment.canReschedule && !appointment.canCancel && (
                      <p className="text-sm text-themed-secondary text-center">
                        This appointment can no longer be changed online.
                        {bookingPage?.businessName && (
                          <> Please contact <span className="font-medium text-themed-primary">{bookingPage.businessName}</span> for help.</>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancel Form Modal */}
        <Dialog
          open={showCancelForm}
          onClose={() =>
            !cancelMutation.isPending && setShowCancelForm(false)
          }
          className="relative z-50"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm transition-opacity duration-300 ease-editorial data-[closed]:opacity-0"
          />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <DialogPanel
                transition
                style={themedBg.style}
                className="relative w-full max-w-lg bg-themed-card border-themed rounded-card border shadow-lift p-6 transition-all duration-300 ease-editorial data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[closed]:scale-95"
              >
                <DialogTitle
                  as="h2"
                  className="font-display text-xl text-themed-primary mb-2"
                >
                  Cancel appointment
                </DialogTitle>
                <p className="text-themed-secondary mb-4">
                  Are you sure you want to cancel? This can't be undone.
                </p>

                {bookingPage?.cancellationPolicy && (
                  <div
                    className={`mb-4 p-3 rounded-card border ${
                      isDark
                        ? "bg-signal-amber-500/20 border-signal-amber-500"
                        : "bg-signal-amber-50 border-signal-amber-100"
                    }`}
                  >
                    <h3
                      className={`text-sm font-medium mb-1 ${
                        isDark ? "text-signal-amber-100" : "text-signal-amber-600"
                      }`}
                    >
                      Cancellation policy
                    </h3>
                    <p
                      className={`text-sm whitespace-pre-wrap ${
                        isDark ? "text-signal-amber-100" : "text-signal-amber-600"
                      }`}
                    >
                      {bookingPage.cancellationPolicy}
                    </p>
                  </div>
                )}

                <div className="mb-6">
                  <label
                    htmlFor="cancellation-reason"
                    className="block text-sm font-medium text-themed-secondary mb-2"
                  >
                    Reason for cancellation{" "}
                    <span className="font-normal text-themed-secondary">
                      (optional — shared with{" "}
                      {bookingPage?.businessName || "the business"})
                    </span>
                  </label>
                  <textarea
                    id="cancellation-reason"
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-themed rounded-md focus:outline-none focus-themed bg-themed-card text-themed-primary"
                    placeholder="Please let us know why you're cancelling..."
                  />
                </div>

                {cancelMutation.error && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mb-4 p-3 bg-signal-red-50 border border-signal-red-100 rounded-md"
                  >
                    <p className="text-sm text-signal-red-700">
                      {cancelMutation.error.message ||
                        "Something went wrong. Please try again."}
                    </p>
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={cancelMutation.isPending}
                      className="mt-2 text-xs font-medium text-signal-red-700 underline hover:text-signal-red-600 disabled:opacity-50"
                    >
                      Try again
                    </button>
                  </div>
                )}

                <div className="space-y-3">
                  <ThemedButton
                    onClick={handleCancel}
                    disabled={cancelMutation.isPending}
                    primaryColor={primaryColor}
                    themeMode={themeMode}
                    variant="destructive"
                    className="w-full"
                  >
                    {cancelMutation.isPending
                      ? "Cancelling..."
                      : "Cancel appointment"}
                  </ThemedButton>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowCancelForm(false)}
                      disabled={cancelMutation.isPending}
                      className="text-sm text-themed-secondary hover:text-themed-primary hover:underline transition-colors disabled:opacity-50 px-3 py-3 rounded-pill focus:outline-none focus-themed"
                    >
                      Keep appointment
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>

        {/* Reschedule Confirmation Modal (F-16) */}
        <Dialog
          open={showRescheduleConfirmModal}
          onClose={() => setShowRescheduleConfirmModal(false)}
          className="relative z-50"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm transition-opacity duration-300 ease-editorial data-[closed]:opacity-0"
          />
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <DialogPanel
                transition
                style={themedBg.style}
                className="relative w-full max-w-lg bg-themed-card border-themed rounded-card border shadow-lift p-6 transition-all duration-300 ease-editorial data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[closed]:scale-95"
              >
                <DialogTitle
                  as="h2"
                  className="font-display text-xl text-themed-primary mb-2"
                >
                  Confirm reschedule
                </DialogTitle>
                <p className="text-themed-secondary mb-4">
                  Your appointment will be moved to the selected time. A confirmation email will be sent to you.
                </p>

                {appointment && selectedDate && selectedTime && (
                  <div className="mb-6 space-y-3 rounded-card border border-themed p-3 bg-themed-card">
                    <div>
                      <div className="smallcaps text-themed-secondary mb-1">
                        From
                      </div>
                      <div className="text-sm text-themed-secondary line-through">
                        {formatDateTime(
                          appointment.startTime,
                          appointment.timeZoneId
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="smallcaps text-themed-secondary mb-1">
                        To
                      </div>
                      <div className="text-sm font-medium text-themed-primary">
                        {formatSelectedDateTime(selectedDate, selectedTime)}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                  <ThemedButton
                    onClick={() => setShowRescheduleConfirmModal(false)}
                    primaryColor={primaryColor}
                    themeMode={themeMode}
                    variant="secondary"
                  >
                    Go back
                  </ThemedButton>
                  <ThemedButton
                    onClick={() => {
                      setShowRescheduleConfirmModal(false);
                      handleSubmitReschedule();
                    }}
                    primaryColor={primaryColor}
                    themeMode={themeMode}
                  >
                    Confirm reschedule
                  </ThemedButton>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </div>
    </PublicBookingLayout>
  );
};

export default AppointmentManagementPage;
